import React, { useState } from 'react';
import {
  Alert,
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Modal,
  message,
  Descriptions,
  Tabs,
  Progress
} from 'antd';
import {
  ArrowLeftOutlined,
  ExportOutlined,
  CheckOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { getBillStatusColor, getBillStatusLabel } from '../../../utils/billI18n';
import { renderBillTypeTag, getBillTypeLabel } from '../../../utils/billTypeConfig';
import {
  GET_BILL_DETAIL,
  GET_BILL_STATUS_TRANSITION_RECORDS
} from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {
  CONFIRM_BILL_INTERNAL,
  EXPORT_BILL,
  CANCEL_BILL_SUMMARY
} from '../../../graphql/mutations/settlement/settlementBillMutations';
import { GET_EXPORT_HISTORY, GET_EXPORT_FILE_DOWNLOAD_URL } from '../../../graphql/queries/settlement/billExportQueries';
import { GET_BILL_SUMMARY_HISTORY } from '../../../graphql/queries/settlement/billSummaryQueries';
import { BillExportModal, type ExportLanguage } from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import BillSummaryProgressModal from '../../../components/settlement/BillSummaryProgressModal';
import AdjustmentForm from '../../../components/settlement/AdjustmentForm';
import AdjustmentRecordList from '../../../components/settlement/AdjustmentRecordList';
import BillingPeriodRuleCard from '../../../components/settlement/BillingPeriodRuleCard';
import DspBillPaymentInfoCard from '../../../components/settlement/DspBillPaymentInfoCard';
import DspInvoiceCard from '../../../components/settlement/DspInvoiceCard';
import BillAttachmentPanel from '../../../components/settlement/BillAttachmentPanel';
import { useDspBillPaymentInfo } from '../../../graphql/queries/settlement/dspBillPayment';
import { GET_DSP_INVOICE_BY_BILL_ID } from '../../../graphql/queries/settlement/dspInvoiceQueries';
import type { DspInvoiceType } from '../../../graphql/types/settlement/dspInvoice';
import { formatPacificTime } from '../../../utils/dashboardPacificTime';
import { formatCentsAmount } from '../../../utils/invoiceAmount';
import { downloadBillExportFile, formatFileSize, formatExportStatus } from '../../../utils/billExportDownload';
import { formatFileType, getFileTypeColor } from '../../../utils/fileTypeFormatter';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import { canExportBill, getExportButtonTitle } from '../../../utils/billExportPermission';
import { formatSettlementAmountFromCents, formatSettlementAmountFromMajor } from '../../../utils/settlementAmountFormatter';
import type { AdjustmentAttachmentDraft } from '../../../utils/settlementAdjustmentAttachment';
import { getUserInfo } from '../../../utils/auth';
import type {
  SettlementBillDetailResponseType,
  BillFeeSummaryType,
  BillExportTemplateType
} from '../../../graphql/types/settlement';
import type { BillExportHistoryType } from '../../../graphql/types/settlement/BillExportTypes';
import type { BillSummaryHistory, BillStatusTransitionRecord } from '../../../graphql/types/settlement/settlementBillTypes';
import { useTranslation } from 'react-i18next';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
  GET_DSP_ADJUST_BILL_TASK,
  DspAdjustBillTask,
  GetDspAdjustBillTaskData,
  GetDspAdjustBillTaskVariables,
} from './graphql/queries/getDspAdjustBillTask';
import { DspAdjustBillTaskModal } from './components/DspAdjustBillTaskModal';
import { DspAdjustBillHistoryDrawer } from './components/DspAdjustBillHistoryDrawer';
import { HasResource } from 'components/HasResource';
import { WORKFLOW_BIZ_TYPE_DSP_SUMMARY_ADJUSTMENT } from './constants/workflowBizTypes';
import {
  isWorkflowActiveInstanceError,
  resolveWorkflowErrorMessage,
} from './utils/workflowErrorMessage';
import { resolveWorkflowMessage } from '../../../utils/graphqlUtils';
import { hideZeroDepositInsuranceRows } from './depositInsurance/hideZeroDepositInsuranceRows';

/** DSP账单详情页面 */
const INITIATE_WORKFLOW_APPROVAL = gql`
  mutation InitiateWorkflowApproval($bizType: String!, $bizId: Long!, $contextJson: String!) {
    initiateWorkflowApproval(bizType: $bizType, bizId: $bizId, contextJson: $contextJson) {
      id
      instanceStatus
      success
      message
      messageKey
    }
  }
`;

interface PendingAdjustmentDraft {
  feeItemCode: string;
  adjustmentAmount: string;
  beforeAmount: string;
  afterAmount: string;
  reason: string;
  feeItemName: string;
  attachment: AdjustmentAttachmentDraft | null;
}

const APPROVAL_ALLOWED_BILL_STATUSES = new Set(['INTERNAL_ALREADY', 'EXTERNAL_REJECTED']);

/**
 * 已过 Invoice 确认环节的账单，付款信息无条件对财务展示 ——
 * 这两个状态要么本就是财务确认 Invoice 之后到达的，要么属于 Invoice 线上化之前的存量账单，
 * 后者没有 Invoice 可依，用 invoiceSubmitted 判断会把它们的付款信息与付款凭证一并藏掉。
 */
const PAYMENT_INFO_ALWAYS_VISIBLE_STATUSES = new Set(['INVOICE_CONFIRMED', 'PAID']);

const DspSettlementBillDetail: React.FC = () => {
  const { t } = useTranslation();
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  const [summaryProgressModalVisible, setSummaryProgressModalVisible] = useState(false);
  const [summaryTaskId, setSummaryTaskId] = useState<string | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>('adjustment');
  const [pendingAdjustments, setPendingAdjustments] = useState<Record<string, PendingAdjustmentDraft>>({});
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [preCheckLoading, setPreCheckLoading] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_BILL_DETAIL, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      message.error('查询DSP账单详情失败: ' + err.message);
    }
  });

  const { data: taskData, refetch: refetchTask } = useQuery<
    GetDspAdjustBillTaskData,
    GetDspAdjustBillTaskVariables
  >(GET_DSP_ADJUST_BILL_TASK, {
    variables: { billId: billId ?? "" },
    skip: !billId,
  });
  const latestTask: DspAdjustBillTask | null =
    taskData?.getDspAdjustBillTask ?? null;
  const isTaskInProgress =
    latestTask !== null &&
    (latestTask.status === 0 || latestTask.status === 1);

  const [loadExportHistory, { data: exportHistoryData, loading: exportHistoryLoading }] = useLazyQuery(
    GET_EXPORT_HISTORY,
    {
      variables: {
        billId: billId,
        clientId: null,
        pageRequest: { page: 1, pageSize: 20 }
      },
      fetchPolicy: 'network-only'
    }
  );

  const [getExportFileDownloadUrl] = useLazyQuery(GET_EXPORT_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only'
  });

  const [loadSummaryHistory, { data: summaryHistoryData, loading: summaryHistoryLoading }] = useLazyQuery(
    GET_BILL_SUMMARY_HISTORY,
    {
      variables: { billId: billId },
      fetchPolicy: 'network-only'
    }
  );
  const [loadBillStatusTransitionRecords, { data: billStatusTransitionData, loading: billStatusTransitionLoading }] = useLazyQuery(
    GET_BILL_STATUS_TRANSITION_RECORDS,
    {
      variables: { billId: billId },
      fetchPolicy: 'network-only'
    }
  );

  const [confirmBill] = useMutation(CONFIRM_BILL_INTERNAL, {
    refetchQueries: ['GetBillDetail'],
    awaitRefetchQueries: true
  });
  const [exportBill] = useMutation(EXPORT_BILL);
  const [cancelBillSummary] = useMutation(CANCEL_BILL_SUMMARY);
  const [initiateWorkflowApproval, { loading: initiatingApproval }] = useMutation(INITIATE_WORKFLOW_APPROVAL);

  const canOperate = hasResource([ResourceCode.settlement.SETTLEMENT_COST_BILL_OPERATE]);
  const canConfirmInvoice = hasResource([ResourceCode.settlement.SETTLEMENT_DSP_BILL_INVOICE_CONFIRM]);
  const canPay = hasResource([ResourceCode.settlement.SETTLEMENT_DSP_BILL_PAYMENT]);

  // DSP 账单付款信息（Invoice 确认 / 付款 / 凭证）
  const {
    data: paymentInfoData,
    loading: paymentInfoLoading,
    refetch: refetchPaymentInfo,
  } = useDspBillPaymentInfo(billId);

  const billDetail: SettlementBillDetailResponseType | undefined = data?.getBillDetail;

  // 与 DspInvoiceCard 同 query 同变量，Apollo 合并请求、共享缓存；总部接口只返回已提交的 Invoice
  const { data: invoiceData } = useQuery(GET_DSP_INVOICE_BY_BILL_ID, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    skip: !billId || !billDetail?.bill?.invoiceEligible,
  });
  const invoice: DspInvoiceType | null = invoiceData?.getDspInvoiceByBillId ?? null;

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
    if (key === 'billStatusTransition') loadBillStatusTransitionRecords();
    if (key === 'export') loadExportHistory();
    if (key === 'summary') loadSummaryHistory();
  };

  const handleRefreshExportHistory = () => loadExportHistory();
  const handleRefreshSummaryHistory = () => loadSummaryHistory();
  const handleRefreshBillStatusTransition = () => loadBillStatusTransitionRecords();

  const handleViewSummaryProgress = (taskId: string) => {
    setSummaryTaskId(taskId);
    setSummaryProgressModalVisible(true);
  };

  const handleCancelSummary = async (taskId: string) => {
    Modal.confirm({
      title: '确认取消汇总任务',
      content: '确定要取消这个汇总任务吗？取消后任务将停止执行。',
      okText: '确认取消',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await cancelBillSummary({ variables: { taskId } });
          if (result.data?.cancelBillSummary?.success) {
            message.success('汇总任务已取消');
            loadSummaryHistory();
            refetch();
          } else {
            message.error(result.data?.cancelBillSummary?.message || '取消汇总任务失败');
          }
        } catch (err) {
          message.error('取消汇总任务失败: ' + (err as Error).message);
        }
      }
    });
  };

  const handleSummaryCompleted = (success: boolean) => {
    if (success) {
      message.success('账单汇总完成！');
      loadSummaryHistory();
      refetch();
    } else {
      message.error('账单汇总失败！');
    }
  };

  const handleCollectAdjustmentDraft = (
    feeItemId: string,
    feeItemName: string,
    adjustmentAmount: string,
    reason: string,
    beforeAmount: string,
    afterAmount: string,
    attachment: AdjustmentAttachmentDraft | null
  ) => {
    setPendingAdjustments(prev => ({
      ...prev,
      [feeItemId]: {
        feeItemCode: feeItemId,
        adjustmentAmount,
        beforeAmount,
        afterAmount,
        reason,
        feeItemName,
        attachment,
      }
    }));
  };

  const handleRemovePendingAdjustment = (feeItemId: string) => {
    setPendingAdjustments(prev => {
      const next = { ...prev };
      delete next[feeItemId];
      return next;
    });
  };

  const handleInitiateApproval = async () => {
    const billStatus = billDetail?.bill?.status;
    if (!billStatus || !APPROVAL_ALLOWED_BILL_STATUSES.has(billStatus)) {
      message.warning('当前账单状态不支持发起审批，仅账单已出账和账单已申诉驳回可发起审批');
      return;
    }
    const items = Object.values(pendingAdjustments);
    if (items.length === 0) {
      message.warning('请先添加至少一条调账草稿');
      return;
    }
    try {
      const contextJson = JSON.stringify({
        adjustmentItems: items.map(item => ({
          feeItemCode: item.feeItemCode,
          feeItemName: item.feeItemName,
          adjustmentAmount: item.adjustmentAmount,
          beforeAmount: item.beforeAmount,
          afterAmount: item.afterAmount,
          reason: item.reason,
          // 附件文件已直传 S3，这里只携带元数据；审批通过后由回调写入调账记录
          attachment: item.attachment
            ? {
                objectKey: item.attachment.objectKey,
                fileName: item.attachment.fileName,
                fileSize: item.attachment.fileSize,
                contentType: item.attachment.contentType ?? null,
              }
            : null,
        })),
      });
      const result = await initiateWorkflowApproval({
        variables: {
          bizType: WORKFLOW_BIZ_TYPE_DSP_SUMMARY_ADJUSTMENT,
          bizId: billId, // Long 超出 JS 安全整数范围，必须以字符串传输，否则会丢精度
          contextJson,
        }
      });
      if (result.data?.initiateWorkflowApproval?.success === false) {
        message.error(resolveWorkflowMessage(result.data.initiateWorkflowApproval, '发起审批失败，请稍后重试'));
        return;
      }
      if (result.data?.initiateWorkflowApproval?.id) {
        message.success('发起审批成功');
        setPendingAdjustments({});
        return;
      }
      message.error('发起审批失败，请稍后重试');
    } catch (err) {
      const friendlyMessage = resolveWorkflowErrorMessage(err, '发起审批失败，请稍后重试');
      if (isWorkflowActiveInstanceError(err)) {
        message.warning(friendlyMessage);
      } else {
        message.error(friendlyMessage);
      }
    }
  };

  const handleDownloadExport = async (record: BillExportHistoryType) => {
    if (record.status !== 'SUCCESS') {
      message.warning('只能下载成功的导出文件');
      return;
    }
    if (!record.fileName) {
      message.error('未找到导出文件信息');
      return;
    }
    try {
      message.loading({ content: '正在获取下载链接...', key: 'download' });
      const result = await getExportFileDownloadUrl({
        variables: { taskId: record.taskId }
      });
      const downloadUrl = result.data?.getExportFileDownloadUrl;
      if (!downloadUrl) {
        message.error({ content: '获取下载链接失败', key: 'download' });
        return;
      }
      message.success({ content: '开始下载...', key: 'download', duration: 2 });
      downloadBillExportFile(downloadUrl, record.fileName);
    } catch (err) {
      message.error('获取下载链接失败: ' + (err as Error).message);
    }
  };

  const handleConfirmBill = async () => {
    setPreCheckLoading(true);
    try {
      const result = await refetchTask();
      const task = result.data?.getDspAdjustBillTask;
      if (task?.status === 0 || task?.status === 1) {
        message.warning('当前有DSP明细调账任务正在处理中，请等待任务完成后再操作');
        return;
      }
    } finally {
      setPreCheckLoading(false);
    }
    let remark = '';
    Modal.confirm({
      title: '内部确认账单',
      content: (
        <div className="pb-6">
          <div className="mb-2">请填写备注：</div>
          <Input.TextArea
            maxLength={200}
            showCount
            rows={4}
            placeholder="请输入内部确认备注"
            onChange={(e) => {
              remark = e.target.value;
            }}
          />
        </div>
      ),
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        if (!remark.trim()) {
          message.warning('请先填写备注');
          return;
        }
        try {
          const result = await confirmBill({ variables: { billId, remark: remark.trim() } });
          if (result.data?.confirmBillInternal?.success) {
            message.success('账单确认成功');
            await refetch();
            return;
          }
          message.error(result.data?.confirmBillInternal?.errorMessage || '账单确认失败');
          await refetch();
          return;
        } catch (err) {
          message.error('账单确认失败: ' + (err as Error).message);
          await refetch();
          return;
        }
      }
    });
  };

  const handleExportBill = async () => {
    setPreCheckLoading(true);
    try {
      const result = await refetchTask();
      const task = result.data?.getDspAdjustBillTask;
      if (task?.status === 0 || task?.status === 1) {
        message.warning('当前有DSP明细调账任务正在处理中，暂时不能导出账单');
        return;
      }
    } finally {
      setPreCheckLoading(false);
    }
    setExportModalVisible(true);
  };

  const handleExport = async (template: BillExportTemplateType, language: ExportLanguage) => {
    if (!billDetail?.bill) return;
    const bill = billDetail.bill;
    try {
      message.loading({ content: '正在检查导出历史...', key: 'check', duration: 0 });
      const { data: historyData } = await loadExportHistory();
      message.destroy('check');
      const historyRecords = historyData?.getExportHistory?.content || [];
      const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

      if (successRecords.length > 0) {
        const latestRecord = successRecords[0];
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '确认导出账单',
            content: (
              <div>
                <p>该账单已有导出记录，最近一次导出：</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>导出时间: {new Date(latestRecord.createdAt).toLocaleString('zh-CN')}</li>
                  <li>文件名: {latestRecord.fileName}</li>
                  <li>模板类型: {latestRecord.templateType}</li>
                </ul>
                <p style={{ marginTop: 12, color: '#faad14' }}>
                  <strong>提示：</strong>重新导出将生成新的文件，之前的导出记录仍会保留。
                </p>
                <p>确定要继续导出吗？</p>
              </div>
            ),
            okText: '确定导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            width: 520
          });
        });
        if (!confirmResult) return;
      } else {
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '确认导出账单',
            content: (
              <div>
                <p>将为以下账单创建导出任务：</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>账单ID: {bill.id}</li>
                  <li>DSP客户: {bill.clientName}</li>
                  <li>账单类型: {getBillTypeLabel(bill.billType)}</li>
                  <li>账单金额: {formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</li>
                </ul>
                <p style={{ marginTop: 12 }}>确定要导出吗？</p>
              </div>
            ),
            okText: '确定导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmResult) return;
      }

      const result = await exportBill({
        variables: {
          exportRequest: { billId: bill.id, template, format: 'EXCEL', language }
        }
      });
      if (result.data?.exportBill?.success) {
        const taskIds = result.data.exportBill.taskIds || [];
        message.success(`导出任务已创建（${taskIds.length}个文件）`);
        setExportModalVisible(false);
        setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
        setProgressModalVisible(true);
      } else {
        message.error(result.data?.exportBill?.errorMessage || '导出失败');
      }
    } catch (err) {
      message.destroy('check');
      message.error('导出失败: ' + (err as Error).message);
    }
  };

  const handleExportCompleted = (task: any) => {
    if (task.status === 'SUCCESS') message.success('导出成功！');
    else if (task.status === 'FAILED') message.error(`导出失败: ${task.errorMessage || '未知错误'}`);
  };

  if (loading) {
    return <div>加载中...</div>;
  }
  if (!billDetail) {
    return <div>账单不存在</div>;
  }

  const { bill, feeSummaries } = billDetail;
  const exportHistory = exportHistoryData?.getExportHistory?.content || [];
  const summaryHistory = summaryHistoryData?.getBillSummaryHistory || [];
  const billStatusTransitionRecords: BillStatusTransitionRecord[] = billStatusTransitionData?.getBillStatusTransitionRecords || [];
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatTime = (timeString: string | undefined | null) => {
    if (!timeString) return '-';
    return formatTimeWithTimezone(timeString, browserTimeZone);
  };

  const isLABusinessHours = (() => {
    const laHour = parseInt(
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        hour12: false,
      }).format(new Date())
    );
    return laHour >= 0 && laHour < 12;
  })();

  const feeSummaryColumns = [
    {
      title: '费用项名称',
      dataIndex: 'feeItemName',
      key: 'feeItemName',
      width: 220,
      ellipsis: true,
      render: (name: string) => <span title={name}>{name}</span>
    },
    {
      title: '汇总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      render: (amount: string, record: BillFeeSummaryType) => (
        <span style={{ color: record.amountSign === -1 ? '#ff4d4f' : '#52c41a' }}>
          {`${record.amountSign === -1 ? '-' : '+'}${amount}`}
        </span>
      )
    },
    {
      title: '是否可调账',
      dataIndex: 'allowAdjustment',
      key: 'allowAdjustment',
      width: 150,
      render: (allow: boolean) => {
        if (bill.isCanceled) return <Tag color="red">账单已作废不可调账</Tag>;
        return <Tag color={allow ? 'green' : 'red'}>{allow ? '可调账' : '不可调账'}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (record: BillFeeSummaryType) => (
        <AdjustmentForm
          billId={billId!}
          feeItemId={record.feeItemId}
          feeItemName={record.feeItemName}
          currentAmount={Math.round(parseFloat(String(record.totalAmount).replace(/[^\d.-]/g, '')) * 100)}
          allowAdjustment={record.allowAdjustment}
          currency={bill.currency}
          isBillCanceled={bill.isCanceled}
          periodStart={formatTimeWithTimezone(bill.periodStart, bill.timeZone) ?? ''}
          periodEnd={formatTimeWithTimezone(bill.periodEnd, bill.timeZone) ?? ''}
          enableAttachment
          draftMode
          onDraftSubmit={(draft) =>
            handleCollectAdjustmentDraft(
              record.feeItemId,
              record.feeItemName,
              draft.adjustmentAmount,
              draft.reason,
              draft.beforeAmount,
              draft.afterAmount,
              draft.attachment,
            )
          }
          onSuccess={() => undefined}
        />
      )
    }
  ];

  const summaryHistoryColumns = [
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 100 },
    { title: '操作人', dataIndex: 'operatorName', key: 'operatorName', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => {
        const statusConfig: Record<number, { color: string; text: string }> = {
          0: { color: 'default', text: '待处理' },
          1: { color: 'processing', text: '处理中' },
          2: { color: 'success', text: '成功' },
          3: { color: 'error', text: '失败' },
          4: { color: 'warning', text: '已取消' }
        };
        const config = statusConfig[status] || { color: 'default', text: '未知' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: BillSummaryHistory) => {
        const getStatus = () => {
          if (record.status === 1) return 'active';
          if (record.status === 2) return 'success';
          if (record.status === 3 || record.status === 4) return 'exception';
          return 'normal';
        };
        return <Progress percent={progress} status={getStatus()} format={(p) => `${p}%`} size="small" />;
      }
    },
    { title: '当前步骤', dataIndex: 'currentStep', key: 'currentStep', width: 200, ellipsis: true, render: (step: string | undefined) => step || '-' },
    { title: '汇总费用项数', dataIndex: 'summaryCount', key: 'summaryCount', width: 120, render: (c: number | undefined) => c ?? '-' },
    {
      title: '汇总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (amount: number | undefined) => formatSettlementAmountFromCents(amount, bill.currency)
    },
    { title: '创建时间 (本地时区)', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (time: string) => formatTime(time) },
    { title: '完成时间 (本地时区)', dataIndex: 'completedAt', key: 'completedAt', width: 170, render: (time: string | undefined) => formatTime(time) },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 180,
      ellipsis: true,
      render: (msg: string | undefined) => (!msg ? '-' : <span style={{ color: '#ff4d4f' }} title={msg}>{msg}</span>)
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: BillSummaryHistory) => {
        if (record.status === 1) {
          return (
            <Space>
              <Button type="link" size="small" onClick={() => handleViewSummaryProgress(record.id)}>查看进度</Button>
              {canOperate && (
                <Button type="link" size="small" danger onClick={() => handleCancelSummary(record.id)}>取消</Button>
              )}
            </Space>
          );
        }
        if (record.status === 0) {
          return canOperate ? (
            <Button type="link" size="small" danger onClick={() => handleCancelSummary(record.id)}>取消</Button>
          ) : '';
        }
        return '-';
      }
    }
  ];

  const billStatusTransitionColumns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time: string) => formatTime(time)
    },
    {
      title: '状态流转',
      key: 'statusTransition',
      width: 260,
      render: (_: any, record: BillStatusTransitionRecord) => (
        <span>
          {record.fromStatusLabel || '-'} {'->'} {record.toStatusLabel}
        </span>
      )
    },
    {
      title: '操作人',
      key: 'operator',
      width: 180,
      render: (_: any, record: BillStatusTransitionRecord) => record.operatorName || record.operatorId || '-'
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 240,
      ellipsis: true,
      render: (remark: string | null | undefined) => (
        <span title={remark || undefined}>{remark || '-'}</span>
      )
    }
  ];

  const exportHistoryColumns = [
    { title: '任务ID', dataIndex: 'taskId', key: 'taskId', width: 80 },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 100,
      render: (fileType: number) => {
        const color = getFileTypeColor(fileType as 0 | 1 | 2);
        return <Tag color={color}>{formatFileType(fileType as 0 | 1 | 2)}</Tag>;
      }
    },
    { title: '文件名', dataIndex: 'fileName', key: 'fileName', width: 250, ellipsis: true },
    { title: '文件大小', dataIndex: 'fileSize', key: 'fileSize', width: 100, render: (size: number | undefined) => (size ? formatFileSize(size) : '-') },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = formatExportStatus(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: BillExportHistoryType) => {
        const getStatus = () => {
          if (record.status === 'RUNNING') return 'active';
          if (record.status === 'SUCCESS') return 'success';
          if (record.status === 'FAILED') return 'exception';
          return 'normal';
        };
        return <Progress percent={progress} status={getStatus()} format={(p) => `${p}%`} size="small" />;
      }
    },
    { title: '当前步骤', dataIndex: 'currentStep', key: 'currentStep', width: 200, ellipsis: true, render: (step: string | undefined) => step || '-' },
    { title: '创建时间 (本地时区)', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (time: string) => formatTime(time) },
    { title: '完成时间 (本地时区)', dataIndex: 'completedAt', key: 'completedAt', width: 170, render: (time: string | undefined) => formatTime(time) },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: BillExportHistoryType) => canOperate ? (
        <Button
          type="link"
          size="small"
          icon={<DownloadOutlined />}
          disabled={record.status !== 'SUCCESS'}
          onClick={() => handleDownloadExport(record)}
        >
          下载
        </Button>
      ) : ''
    }
  ];

  return (
    <div className="p-6">
      {isTaskInProgress && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, cursor: 'pointer' }}
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>明细调账任务正在处理中，暂停账单导出！</span>
              <span style={{ color: '#1677ff' }}>点击了解详情</span>
            </div>
          }
          onClick={() => setTaskModalVisible(true)}
        />
      )}
      <div className="mb-4">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/settlement/bill-list#tab=dsp')}
          className="mb-2"
        >
          返回
        </Button>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">DSP账单详情</h1>
          <Space>
            {bill.invoiceEligible && <DspInvoiceCard billId={billId!} billStatus={bill.status} source="central" billAmount={bill.billAmount} billCurrency={bill.currency} />}
            {canOperate && !bill.isCanceled && (bill.status === 'INTERNAL_ALREADY' || bill.status === 'EXTERNAL_REJECTED') && (
              <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirmBill} disabled={isTaskInProgress || preCheckLoading} >
                内部确认账单
              </Button>
            )}
            {canOperate && (
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportBill}
                disabled={!canExportBill(bill) || isTaskInProgress || preCheckLoading}
                title={isTaskInProgress ? 'DSP调账任务处理中，暂时不能导出账单' : getExportButtonTitle(bill)}
              >
                导出账单
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/*
        自制件的 Invoice 号与金额来自 DSP 上传的原件（解析失败时甚至是人工填写），
        财务需按人工核对流程处理，故在页面级常显提示，而非只挂在 Invoice 弹窗入口的小 Tag 上。
        invoiceSelfMade 由服务端在「已提交」后才置 true，与 Invoice 的可见性口径一致。
      */}
      {bill.invoiceEligible && paymentInfoData?.paymentInfo?.invoiceSelfMade && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dspInvoice.selfMade.centralNotice')}
        />
      )}

      {/* amountMismatch 由后端按「分」整数比对 Invoice 金额与账单结算金额 */}
      {bill.invoiceEligible && invoice?.amountMismatch && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dspInvoice.amountMismatch')}
        />
      )}

      <BillingPeriodRuleCard billingPeriodInstanceId={billDetail?.bill?.billingPeriodInstanceId} />

      {/*
        付款信息整块要等 DSP 提交 Invoice 后才对财务开放：未提交时票还可能改号/换自制件，
        连带的开票快照也会跟着变，此时给财务看付款信息只会造成误核对。
        DSP 端不适用该限制 —— 开票前 DSP owner 正需要核对自己的收款信息。

        账单已过 Invoice 确认环节时无条件展示：Invoice 线上化之前的存量账单根本没有 Invoice，
        只看 invoiceSubmitted 会让这些账单的付款信息与付款凭证对财务彻底不可见。
      */}
      {(paymentInfoData?.paymentInfo?.invoiceSubmitted || PAYMENT_INFO_ALWAYS_VISIBLE_STATUSES.has(bill.status)) && (
        <DspBillPaymentInfoCard
          billId={billId!}
          billStatus={bill.status}
          paymentInfo={paymentInfoData?.paymentInfo}
          loading={paymentInfoLoading}
          invoiceEligible={bill.invoiceEligible}
          canOperate={canOperate && !bill.isCanceled}
          canConfirmInvoice={canConfirmInvoice && !bill.isCanceled}
          canPay={canPay && !bill.isCanceled}
          showPaymentMethod
          canEditPaymentMethod={canPay && !bill.isCanceled && bill.status === 'PAID'}
          onRefresh={() => refetchPaymentInfo()}
          onStatusChanged={() => refetch()}
        />
      )}

      <Card title="账单信息" className="mb-4">
        <Descriptions column={2}>
          <Descriptions.Item label="账单ID">{bill.id}</Descriptions.Item>
          <Descriptions.Item label="DSP客户">{bill.clientName}</Descriptions.Item>
          {/* 非 DSP 账单或 DSP 未维护公司全称时下发 null，降级为 '-' */}
          <Descriptions.Item label="FULL NAME">{bill.dspFullName || '-'}</Descriptions.Item>
          <Descriptions.Item label="结算客户ID">{bill.clientId}</Descriptions.Item>
          <Descriptions.Item label="ClientID">{bill.channelId}</Descriptions.Item>
          <Descriptions.Item label="账单类型">{renderBillTypeTag(bill.billType)}</Descriptions.Item>
          <Descriptions.Item label="账单节奏">{bill.settlementCadence ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="账单状态">
            {bill.isCanceled ? (
              <Tag color={getBillStatusColor('CANCELED')}>{getBillStatusLabel('CANCELED')}</Tag>
            ) : (
              // 统一走 billI18n 的状态映射，避免每新增一个状态就要在页面里补一次三元链
              <Tag color={getBillStatusColor(bill.status)}>{getBillStatusLabel(bill.status)}</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="账单金额">
            <span className="text-lg font-bold text-red-600">{formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</span>
          </Descriptions.Item>
          {bill.invoiceEligible && invoice ? (
            <Descriptions.Item label="Invoice金额">
              {/* 账单金额已是红色，不一致时改用橙色区分 */}
              <span className={invoice.amountMismatch ? 'text-lg font-bold text-orange-500' : 'text-lg font-bold'}>
                {formatCentsAmount(invoice.invoiceAmount, invoice.currency)}
              </span>
            </Descriptions.Item>
          ) : null}
          {bill.orderCount != null ? (
            <Descriptions.Item label="订单数量">{Number(bill.orderCount)}</Descriptions.Item>
          ) : null}
          <Descriptions.Item label={`账单周期 (${bill.timeZone})`}>
            {formatTimeWithTimezone(bill.periodStart, bill.timeZone)} ~ {formatTimeWithTimezone(bill.periodEnd, bill.timeZone)}
          </Descriptions.Item>
          <Descriptions.Item label="账单时区">{bill.timeZone}</Descriptions.Item>
          <Descriptions.Item label="创建时间 (本地时区)">
            {bill.createdAt ? new Date(bill.createdAt).toLocaleString('zh-CN', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间 (本地时区)">
            {bill.updatedAt ? new Date(bill.updatedAt).toLocaleString('zh-CN', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Invoice提交时间 (洛杉矶时间)">
            {formatPacificTime(paymentInfoData?.paymentInfo?.invoiceSubmittedAt)}
          </Descriptions.Item>
          <Descriptions.Item label="付款信息变更时间 (洛杉矶时间)">
            {formatPacificTime(paymentInfoData?.paymentInfo?.financeInfoUpdatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="费用汇总" className="mb-4"  
        extra={(bill.status === 'INTERNAL_ALREADY' ||
                  bill.status === 'EXTERNAL_REJECTED') && isLABusinessHours && (
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_DETAIL_ADJUSTMENT]}>
                  <Button
                    onClick={() => setHistoryDrawerVisible(true)}
                  >
                    明细调账
                  </Button>
                </HasResource>
                )}
      >
        <div className="max-w-full overflow-hidden">
          <Table
            columns={feeSummaryColumns}
            dataSource={hideZeroDepositInsuranceRows(feeSummaries)}
            rowKey="feeItemId"
            pagination={false}
            size="small"
            className="mb-4"
            scroll={{ x: 630 }}
            tableLayout="fixed"
          />
        </div>
        <div className="mt-4">
          <Card size="small" title="待审批调账清单">
            <div className="max-w-full overflow-hidden">
              <Table
                rowKey="feeItemCode"
                size="small"
                pagination={false}
                dataSource={Object.values(pendingAdjustments)}
                locale={{ emptyText: '暂无待审批调账项' }}
                scroll={{ x: 960 }}
                tableLayout="fixed"
                columns={[
                  { title: '费用项Code', dataIndex: 'feeItemCode', key: 'feeItemCode', width: 140, ellipsis: true },
                  { title: '费用项名称', dataIndex: 'feeItemName', key: 'feeItemName', width: 180, ellipsis: true },
                  {
                    title: '调账金额(美元)',
                    dataIndex: 'adjustmentAmount',
                    key: 'adjustmentAmount',
                    width: 140,
                    render: (amount: string) => formatSettlementAmountFromCents(Number(amount), bill.currency),
                  },
                  { title: '原因', dataIndex: 'reason', key: 'reason', width: 200, ellipsis: true },
                  {
                    title: '附件',
                    dataIndex: 'attachment',
                    key: 'attachment',
                    width: 200,
                    ellipsis: true,
                    render: (attachment: AdjustmentAttachmentDraft | null) =>
                      attachment ? <span title={attachment.fileName}>{attachment.fileName}</span> : '-',
                  },
                  {
                    title: '操作',
                    key: 'op',
                    width: 90,
                    fixed: 'right' as const,
                    render: (_: any, row: PendingAdjustmentDraft) => (
                      <Button type="link" danger onClick={() => handleRemovePendingAdjustment(row.feeItemCode)}>
                        移除
                      </Button>
                    )
                  }
                ]}
              />
            </div>
              {canOperate && (
                  <div className="mt-3 flex justify-end">
                      <Button
                          type="primary"
                          loading={initiatingApproval}
                          disabled={Object.keys(pendingAdjustments).length === 0 || !APPROVAL_ALLOWED_BILL_STATUSES.has(bill.status)}
                          onClick={handleInitiateApproval}
                      >
                          发起审批
                      </Button>
                  </div>
              )}
          </Card>
        </div>
      </Card>

      <Card className="mb-4">
        <Tabs activeKey={activeTabKey} onChange={handleTabChange} items={[
          {
            key: 'billStatusTransition',
            label: '账单状态流转记录',
            children: (
              <div className="max-w-full overflow-hidden">
                <div className="mb-4 flex justify-end">
                  <Button icon={<ReloadOutlined />} onClick={handleRefreshBillStatusTransition} loading={billStatusTransitionLoading}>刷新</Button>
                </div>
                <Table
                  columns={billStatusTransitionColumns}
                  dataSource={billStatusTransitionRecords}
                  rowKey="id"
                  loading={billStatusTransitionLoading}
                  pagination={{ showSizeChanger: true, showTotal: (total) => t('table.pagination.totalItems', { total }), defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'] }}
                  size="small"
                  scroll={{ x: 860 }}
                  tableLayout="fixed"
                />
              </div>
            )
          },
          {
            key: 'adjustment',
            label: '调账记录',
            children: <AdjustmentRecordList billId={billId!} currency={bill.currency} onRefresh={() => refetch()} />
          },
          {
            key: 'summary',
            label: '汇总历史',
            children: (
              <div className="max-w-full overflow-hidden">
                <div className="mb-4 flex justify-end">
                  <Button icon={<ReloadOutlined />} onClick={handleRefreshSummaryHistory} loading={summaryHistoryLoading}>刷新</Button>
                </div>
                <Table
                  columns={summaryHistoryColumns}
                  dataSource={summaryHistory}
                  rowKey="id"
                  loading={summaryHistoryLoading}
                  pagination={{ showSizeChanger: true, showTotal: (total) => t('table.pagination.totalItems', { total }), defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'] }}
                  size="small"
                  scroll={{ x: 1460 }}
                  tableLayout="fixed"
                />
              </div>
            )
          },
          {
            key: 'attachment',
            label: '附件',
            children: (
              <BillAttachmentPanel billId={billId!} billStatus={bill.status} readOnly={!canOperate} />
            )
          },
          {
            key: 'export',
            label: '导出记录',
            children: (
              <div className="max-w-full overflow-hidden">
                <div className="mb-4 flex justify-end">
                  <Button icon={<ReloadOutlined />} onClick={handleRefreshExportHistory} loading={exportHistoryLoading}>刷新</Button>
                </div>
                <Table
                  columns={exportHistoryColumns}
                  dataSource={exportHistory}
                  rowKey="taskId"
                  loading={exportHistoryLoading}
                  pagination={{ showSizeChanger: true, showTotal: (total) => t('table.pagination.totalItems', { total }), defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'] }}
                  size="small"
                  scroll={{ x: 1400 }}
                  tableLayout="fixed"
                />
              </div>
            )
          }
        ]} />
      </Card>

      <BillExportModal visible={exportModalVisible} bill={bill} onCancel={() => setExportModalVisible(false)} onExport={handleExport} />
      <BillExportProgressModal
        taskId={exportTaskId}
        visible={progressModalVisible}
        onClose={() => { setProgressModalVisible(false); setExportTaskId(null); }}
        onCompleted={handleExportCompleted}
      />
      {summaryTaskId && (
        <BillSummaryProgressModal
          visible={summaryProgressModalVisible}
          taskId={summaryTaskId}
          onClose={() => { setSummaryProgressModalVisible(false); setSummaryTaskId(null); }}
          onCompleted={handleSummaryCompleted}
        />
      )}
      <DspAdjustBillHistoryDrawer
        billId={billId ?? ""}
        visible={historyDrawerVisible}
        onClose={() => setHistoryDrawerVisible(false)}
        isTaskInProgress={isTaskInProgress}
      />
      {taskModalVisible && latestTask && (
        <DspAdjustBillTaskModal
          task={latestTask}
          visible={taskModalVisible}
          onClose={() => setTaskModalVisible(false)}
        />
      )}
    </div>
  );
};

export default DspSettlementBillDetail;
